import { getAllData } from 'apis/customApiHandler';
import Spinner from 'components/Spinner';
import AddUser from 'components/users/addUser';
import ViewUserList, { capitalize } from 'components/users/viewUserList';
import useDebounce from 'hooks/useDebounce';
import { useState } from 'react';
import { BiCloudDownload } from 'react-icons/bi';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { Button, Input, Select } from 'styled';
import { TH } from 'styled/table';
import checkPermission from 'utils/checkPermission';
import exportExcel from 'utils/exportExcel';
import getFormattedDate, { convertToIndianTimezoneAndGetDate } from 'utils/helpers';
import permissions from 'utils/permissionConstants';

const Users = () => {
  const [showAddmodel, setShowAddmodel] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [lastLoginFrom, setLastLoginFrom] = useState('');
  const [lastLoginTo, setLastLoginTo] = useState('');
  const currentUser = useSelector((state) => state.user.currentUser);

  const debouncedValue = useDebounce(search, 1000);
  const { data: users, isLoading } = useQuery(
    ['users', debouncedValue, status, lastLoginFrom, lastLoginTo],
    () =>
      getAllData('/user', {
        searchQuery: debouncedValue,
        status,
        ...(lastLoginFrom && { lastLoginFrom }),
        ...(lastLoginTo && { lastLoginTo }),
      })
  );

  const handleDownload = () => {
    exportExcel(
      users?.data?.map((user, index) => ({
        'S.N.': index + 1,
        'Full Name': user.name,
        Email: user.email,
        Phone: user.phone,
        'User Role': user?.userRole?.name,
        Status: user.active ? 'Active' : 'Inactive',
        'Created At': user.createdAt
          ? new Date(user.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')
          : '',
        'Last Login': user.lastLogin
          ? convertToIndianTimezoneAndGetDate(user.lastLogin, true)
          : '',
        'Last Updated By': user.lastUpdatedBy ? capitalize(user.lastUpdatedBy) : '',
      })) || [],
      'users'
    );
  };

  return (
    <div>
      <div className="mx-10 my-2">
        <div className="mt-8 flex justify-start gap-4 items-end flex-wrap lg:flex-nowrap">
          <div className="relative w-full">
            <Input
              type="search"
              placeholder="Search Here"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
          <label className="w-full text-gray-600 font-normal text-sm shrink-0 lg:w-44">
            Status
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </Select>
          </label>
          <label className="w-full text-gray-600 font-normal text-sm shrink-0 lg:w-44">
            Last Login From
            <Input
              type="date"
              value={lastLoginFrom}
              max={lastLoginTo || getFormattedDate()}
              onChange={(e) => setLastLoginFrom(e.target.value)}
            />
          </label>
          <label className="w-full text-gray-600 font-normal text-sm shrink-0 lg:w-44">
            Last Login To
            <Input
              type="date"
              value={lastLoginTo}
              min={lastLoginFrom}
              max={getFormattedDate()}
              onChange={(e) => setLastLoginTo(e.target.value)}
            />
          </label>
          {users?.data?.length > 0 && (
            <BiCloudDownload
              className="w-10 h-10 text-green-500 hover:scale-105 cursor-pointer text-center shrink-0"
              title="Download"
              onClick={handleDownload}
            />
          )}
          <Button
            className="!px-8 disabled:bg-gray-400 shrink-0"
            bgPrimary
            disabled={!checkPermission([permissions.manage_users], currentUser)}
            onClick={() => setShowAddmodel(true)}
          >
            Add
          </Button>
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <TH scope="col" className='whitespace-nowrap'>S.N.</TH>
                      <TH scope="col">Full Name</TH>
                      <TH scope="col">Email</TH>
                      <TH scope="col">Phone</TH>
                      <TH scope="col">User Role</TH>
                      <TH scope="col" className='whitespace-nowrap'>Status</TH>
                      <TH scope="col" className='whitespace-nowrap'>Created At</TH>
                      <TH scope="col" className='whitespace-nowrap'>Last Login</TH>
                      <TH scope="col" className='whitespace-nowrap'>Last Updated By</TH>
                      <TH scope="col">Actions</TH>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users?.data &&
                      users?.data.map((user, index) => (
                        <ViewUserList
                          user={user}
                          key={user._id}
                          index={index}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddmodel && <AddUser handleClick={() => setShowAddmodel(false)} />}
    </div>
  );
};

export default Users;
